import { router } from "@inertiajs/react";
import { useState } from "react";
import Header from "../Component/Header";

const getCurrentLocalDate = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
};

const formatCurrency = (value) =>
    new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    }).format(Number(value) || 0);

export default function Index({ data = [], filters = {} }) {
    const [dateFrom, setDateFrom] = useState(filters?.date_from ?? getCurrentLocalDate());
    const [dateTo, setDateTo] = useState(filters?.date_to ?? getCurrentLocalDate());

    const statusBadgeClass = (status) => {
        const normalized = String(status || "").toLowerCase();

        if (normalized === "success" || normalized === "berhasil") {
            return "bg-green-100 text-green-700";
        }

        if (normalized === "pending") {
            return "bg-amber-100 text-amber-700";
        }

        if (normalized === "expired" || normalized === "failed") {
            return "bg-red-100 text-red-700";
        }

        return "bg-slate-100 text-slate-700";
    };

    const applyDateFilter = (event) => {
        event.preventDefault();

        router.get(
            "/panel/payments",
            {
                date_from: dateFrom || undefined,
                date_to: dateTo || undefined,
            },
            {
                preserveState: true,
                preserveScroll: true,
                replace: true,
            }
        );
    };

    const resetDateFilter = () => {
        const today = getCurrentLocalDate();
        setDateFrom(today);
        setDateTo(today);
        router.get(
            "/panel/payments",
            {
                date_from: today,
                date_to: today,
            },
            {
                preserveState: true,
                preserveScroll: true,
                replace: true,
            }
        );
    };

    return (
        <>
            <Header />
            <main id="content" className="pb-14 sm:pb-16">
                <div className="max-w-[85rem] px-4 sm:px-6 lg:px-8 mx-auto">
                    <div className="py-2 sm:pb-0 sm:pt-5 space-y-5">
                        <div className="flex flex-wrap justify-between items-center gap-3">
                            <div>
                                <h1 className="text-lg md:text-xl font-semibold text-foreground">Payment</h1>
                                <p className="text-sm text-muted-foreground-1">List of payment transactions and current payment status.</p>
                            </div>
                        </div>

                        <div className="bg-card border border-card-line rounded-xl shadow-2xs p-5">
                            <form onSubmit={applyDateFilter} className="mb-4 flex flex-wrap items-end gap-2">
                                <div>
                                    <label className="block mb-1 text-xs text-muted-foreground-1">From</label>
                                    <input
                                        type="date"
                                        value={dateFrom}
                                        onChange={(event) => setDateFrom(event.target.value)}
                                        className="py-2 px-3 block bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground"
                                    />
                                </div>
                                <div>
                                    <label className="block mb-1 text-xs text-muted-foreground-1">To</label>
                                    <input
                                        type="date"
                                        value={dateTo}
                                        onChange={(event) => setDateTo(event.target.value)}
                                        className="py-2 px-3 block bg-layer border border-layer-line rounded-lg text-sm text-layer-foreground"
                                    />
                                </div>
                                <button
                                    type="submit"
                                    className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-primary border border-primary-line text-primary-foreground hover:bg-primary-hover"
                                >
                                    Apply
                                </button>
                                <button
                                    type="button"
                                    onClick={resetDateFilter}
                                    className="py-2 px-3 inline-flex items-center gap-x-2 text-sm font-medium rounded-lg bg-layer border border-layer-line text-layer-foreground hover:bg-layer-hover"
                                >
                                    Reset
                                </button>
                            </form>

                            <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-table-line">
                                    <thead>
                                        <tr>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Code</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">TRX ID</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Total</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Payment Status</th>
                                            <th className="py-2.5 px-4 text-start text-sm font-normal text-muted-foreground-1">Wallet Lock</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-table-line">
                                        {data.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} className="py-6 px-4 text-center text-sm text-muted-foreground-2">
                                                    No payments found.
                                                </td>
                                            </tr>
                                        ) : (
                                            data.map((payment) => (
                                                <tr key={payment.pay_id} className="hover:bg-surface">
                                                    <td className="py-3 px-4 text-sm text-foreground">{payment.pay_code}</td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">{payment.trx_id ?? "-"}</td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">{formatCurrency(payment.pay_total)}</td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${statusBadgeClass(payment.payment_status_label)}`}>
                                                            {payment.payment_status_label ?? "-"}
                                                        </span>
                                                    </td>
                                                    <td className="py-3 px-4 text-sm text-muted-foreground-2">
                                                        {payment.wallet_lock?.wallet_lock_id ?? "-"}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </>
    );
}
